/* Cookie & privacy banner — backdrop click dismisses the preferences modal.
 *
 * Shopify's hosted storefront-banner.js renders the prefs dialog inside
 * `#shopify-pc__prefs` together with a separate backdrop `#shopify-pc__prefs__overlay`.
 * The overlay is presentational by default — clicks do nothing. We wire a
 * delegated click listener that forwards backdrop clicks to the close button
 * (`#shopify-pc__prefs__header-close`), which is the documented "cancel"
 * pathway: it discards in-flight checkbox edits and keeps whatever consent
 * state was previously saved.
 *
 * Delegation on `document` is necessary because the banner script injects its
 * markup asynchronously after page load — the elements don't exist when this
 * script first runs, so we can't directly attach a listener to the overlay.
 */

(() => {
  const OVERLAY_ID = 'shopify-pc__prefs__overlay';
  const CLOSE_ID = 'shopify-pc__prefs__header-close';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof globalThis.window.Element)) return;
    if (target.id !== OVERLAY_ID) return;

    const closeBtn = document.getElementById(CLOSE_ID);
    if (!closeBtn) return;

    event.preventDefault();
    event.stopPropagation();
    closeBtn.click();
  });
})();

/* Cookie banner copy — shorten Shopify's default Finnish body text.
 *
 * Shopify renders the banner itself (#shopify-pc__banner) and its body copy is
 * NOT editable anywhere we own: it is not in the theme, and
 * Settings → Customer privacy → Cookie banner exposes only colours, position and
 * the checkout toggle — no text field. Verified in admin 27.8.2026. The Admin API
 * has no cookie-banner text resource either. So the only lever is to rewrite the
 * node after Shopify injects it.
 *
 * Why bother: the default Finnish string runs four lines on a 390px phone, and the
 * banner is the first thing a cold ad click sees — it was measured covering roughly
 * half of the first viewport. Every line removed is first-screen space returned to
 * the offer, and consent rate is the multiplier on every Meta optimisation signal
 * this store has.
 *
 * The rewrite keeps the two things that carry legal weight: the purposes
 * (personointi / mainokset / analytiikka) and the statement that none of it happens
 * without consent ("vain hyväksynnälläsi"). The policy link is preserved as the same
 * <a> element — its id and href are Shopify's, we only relabel it.
 *
 * Finnish only. If the storefront ever serves another locale, Shopify's own
 * translated string is left alone rather than replaced with Finnish.
 */
(() => {
  const BANNER_ID = 'shopify-pc__banner';
  const LINK_ID = 'shopify-pc__banner__body-policy-link';
  const BODY =
    'Käytämme evästeitä ja muita teknologioita kokemuksesi personointiin, mainosten ' +
    'esittämiseen ja analytiikan suorittamiseen vain hyväksynnälläsi. ';
  const LINK_LABEL = 'Lisätietoja';

  const isFinnish = () => (document.documentElement.lang || '').toLowerCase().startsWith('fi');

  /* Returns true once the banner exists and has been rewritten, so the observer
     knows it can stop looking for an arrival. */
  const rewrite = () => {
    const banner = document.getElementById(BANNER_ID);
    if (!banner) return false;

    const paragraph = banner.querySelector('.shopify-pc__banner__body p');
    if (!paragraph) return false;
    if (paragraph.dataset.hhCopy === 'short') return true;

    /* Reuse Shopify's own anchor so the href, id and any handlers survive. If the
       markup ever ships without one, fall back to the policy page directly. */
    let link = paragraph.querySelector('a') || document.getElementById(LINK_ID);
    if (!link) {
      link = document.createElement('a');
      link.id = LINK_ID;
      link.href = '/policies/privacy-policy';
    }
    link.textContent = LINK_LABEL;

    paragraph.textContent = BODY;
    paragraph.appendChild(link);
    paragraph.dataset.hhCopy = 'short';
    return true;
  };

  if (!isFinnish()) return;

  /* The banner is injected asynchronously, so it is usually not in the DOM yet.
     A MutationObserver callback runs as a microtask, before the next paint, so the
     original long string should never reach the screen. subtree:true because
     Shopify may nest the banner inside a wrapper rather than append it to <body>. */
  if (rewrite()) return;

  if (typeof globalThis.MutationObserver !== 'function') return;

  const observer = new globalThis.MutationObserver(() => {
    if (rewrite()) observer.disconnect();
  });
  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
